import React, { FC, useCallback, useState } from 'react';
import CustomPopover from 'components/custom-popover/CustomPopover';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
// eslint-disable-next-line max-len
import CreateExternalCompanyModal from 'containers/settings/external-company-settings/components/CreateExternalCompanyModal';
import { DropDownChevronDownIcon, ModalCloseIcon } from 'assets/icons';
import { CreateJobErrorTypes } from 'containers/jobs/constant';
import { getJobValidationErrors } from 'containers/jobs/utils';
import { useAppDispatch, useAppSelector } from 'hooks';
import { removeJobValidationErrors, selectJobValidationErrors } from '../createJobSlice';

interface ExternalCompanyDropdownProps {
  companies: { id: string; name: string }[];
  selectedCompany: { id: string; name: string } | null;
  onSelectCompany: (company: { id: string; name: string }) => void;
}

const ExternalCompanyDropdown: FC<ExternalCompanyDropdownProps> = ({ companies, selectedCompany, onSelectCompany }) => {
  const dispatch = useAppDispatch();

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showCreateExternalCompanyModal, setShowCreateExternalCompanyModal] = useState(false);

  const selectedJobValidations = useAppSelector(selectJobValidationErrors);

  const externalCompanyCreateError = getJobValidationErrors(
    selectedJobValidations,
    CreateJobErrorTypes.CREATE_EXTERNAL_COMPANY
  );

  const handleTogglePopover = () => setIsPopoverOpen(open => !open);
  const handleClosePopover = () => setIsPopoverOpen(false);
  const handleSelectCompany = (company: { id: string; name: string }) => {
    onSelectCompany(company);
    handleClosePopover();
  };

  const onOpenCreateExternalCompanyModal = () => {
    setShowCreateExternalCompanyModal(true);
  };

  const handleCreateExternalCompanyCallback = (company: { id: string; name: string }) => {
    onSelectCompany(company);
    setShowCreateExternalCompanyModal(false);
    dispatch(removeJobValidationErrors(CreateJobErrorTypes.CREATE_EXTERNAL_COMPANY));
  };

  const getInitialData = useCallback(() => {
    if (selectedCompany) {
      return {
        companyName: selectedCompany.name,
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
      };
    }
    return null;
  }, [selectedCompany]);

  const anchorComponent = () => (
    <div
      tabIndex={0}
      aria-label="Choose external company"
      className={
        'flex w-full cursor-pointer items-center justify-between rounded bg-secondaryBtn px-5 py-4 outline-none'
      }
      role="button">
      <span
        className={`text-17px text-primaryText ${selectedCompany ? 'text-primaryText' : 'text-accentText opacity-70'}`}>
        {selectedCompany?.name || 'Choose external company'}
      </span>

      <div className="flex items-center gap-3">
        {selectedCompany && (
          <ModalCloseIcon
            className="h-[16px] w-[16px] text-gray-400"
            aria-hidden="true"
            onClick={e => {
              e.stopPropagation();
              onSelectCompany(null);
            }}
          />
        )}
        <DropDownChevronDownIcon className="h-[8px] w-[14px] text-gray-400" aria-hidden="true" />
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {externalCompanyCreateError && (
        <div className="mt-1 mb-2 text-sbase font-semibold text-error">{externalCompanyCreateError}</div>
      )}
      <div className="w-1/2">
        <CustomPopover
          anchorComponent={anchorComponent}
          show={isPopoverOpen}
          onTogglePopover={handleTogglePopover}
          onClosePopover={handleClosePopover}
          width="100%"
          minWidth="min-w-[320px] w-full"
          padding="p-0"
          margin="mt-2"
          popoverBodyStyle="overflow-y-auto max-h-[400px]"
          containerClassName="w-full"
          highlightAnchor={false}
          showArrow={false}
          disabled={false}
          offset="left-0">
          <ul className="w-full py-2">
            <li>
              <CustomButton
                type={ButtonType.GHOST}
                onClick={onOpenCreateExternalCompanyModal}
                id="create-external-company-button">
                + Create external company
              </CustomButton>
            </li>

            {companies.map(company => (
              <li
                key={company.id}
                tabIndex={0}
                aria-label={`Select ${company.name}`}
                className={
                  'px-4 py-3 text-17px text-primaryText cursor-pointer hover:bg-secondaryBtn outline-none' +
                  (selectedCompany === company ? ' font-semibold text-primaryText' : '')
                }
                onClick={() => handleSelectCompany(company)}
                role="option"
                aria-selected={selectedCompany === company}>
                {company.name}
              </li>
            ))}
          </ul>{' '}
        </CustomPopover>
      </div>

      {externalCompanyCreateError && (
        <CustomButton
          className="p-0"
          type={ButtonType.GHOST}
          onClick={onOpenCreateExternalCompanyModal}
          id="create-external-company-button">
          + Create external company - {selectedCompany?.name}
        </CustomButton>
      )}
      <CreateExternalCompanyModal
        open={showCreateExternalCompanyModal}
        onClose={() => setShowCreateExternalCompanyModal(false)}
        onCreateExternalCompany={handleCreateExternalCompanyCallback}
        initialData={getInitialData()}
      />
    </div>
  );
};

export default ExternalCompanyDropdown;
